/**
 * Card — bg card, border 1 line, radius r3, shadow-sm.
 *
 * Layout sections:
 *   t-card-head  padding 12 16, border-bottom line, flex row space-between
 *                → title h3 md semibold + sub sm ink3 + actions cluster
 *   t-card-body  padding 16
 *   t-card-foot  padding 12 16, border-top line, bg paper2, radius 0 0 r3 r3,
 *                sm ink3, space-between
 *
 * Props
 *   title     — head title (h3 style)
 *   sub       — head subtitle (sm ink3)
 *   actions   — ReactNode placed in a cluster at the right of the head
 *   footer    — ReactNode for the footer row
 *   children  — card body content
 *   padded    — wrap body in padded container (default true)
 *   style     — extra ViewStyle for the outer card
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { useTheme } from '../context/themeContext';

interface CardProps {
  title?: string;
  sub?: string;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  padded?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function Card({
  title,
  sub,
  actions,
  footer,
  children,
  padded = true,
  style,
}: CardProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, spacing, shadows } = theme;

  const hasHead = Boolean(title || sub || actions);

  return (
    <View
      style={[
        {
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: radii.r3,
          overflow: 'hidden',
        },
        shadows.sm as ViewStyle,
        style,
      ]}
    >
      {/* ── Head ─────────────────────────────────────────── */}
      {hasHead && (
        <View
          style={{
            paddingVertical: spacing[3],   // 12
            paddingHorizontal: spacing[4], // 16
            borderBottomWidth: 1,
            borderBottomColor: colors.line,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing[2],
          }}
        >
          {/* Title + sub */}
          <View style={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <Text
                style={{
                  margin: 0,
                  fontSize: fontSize.md,
                  fontWeight: fontWeight.semibold,
                  color: colors.ink,
                  fontFamily: theme.font.sans,
                }}
                numberOfLines={1}
              >
                {title}
              </Text>
            ) : null}
            {sub ? (
              <Text
                style={{
                  fontSize: fontSize.sm,
                  color: colors.ink3,
                  fontFamily: theme.font.sans,
                  marginTop: title ? 1 : 0,
                }}
                numberOfLines={2}
              >
                {sub}
              </Text>
            ) : null}
          </View>

          {/* Actions cluster */}
          {actions ? (
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: spacing[2],
              }}
            >
              {actions}
            </View>
          ) : null}
        </View>
      )}

      {/* ── Body ─────────────────────────────────────────── */}
      {padded ? (
        <View style={{ padding: spacing[4] }}>{children}</View>
      ) : (
        children
      )}

      {/* ── Footer ───────────────────────────────────────── */}
      {footer ? (
        <View
          style={{
            paddingVertical: spacing[3],   // 12
            paddingHorizontal: spacing[4], // 16
            borderTopWidth: 1,
            borderTopColor: colors.line,
            backgroundColor: colors.paper2,
            borderBottomLeftRadius: radii.r3,
            borderBottomRightRadius: radii.r3,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {typeof footer === 'string' ? (
            <Text
              style={{
                fontSize: fontSize.sm,
                color: colors.ink3,
                fontFamily: theme.font.sans,
              }}
            >
              {footer}
            </Text>
          ) : (
            footer
          )}
        </View>
      ) : null}
    </View>
  );
}

export default Card;
